import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * App-wide error boundary. Without this, any render-time throw blanks the whole
 * dashboard (white screen). Catches the error, shows a recoverable fallback, and
 * lets the user reload or go back home instead of losing the session entirely.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      message: error instanceof Error ? error.message : 'An unexpected error occurred.',
    };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for diagnostics; a real telemetry sink can hook here.
    console.error('[urblock] Unhandled UI error:', error, info.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleHome = (): void => {
    window.location.assign('/dashboard/');
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="ub-dark flex min-h-screen items-center justify-center bg-zinc-950 p-6">
        <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-900 p-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10 text-red-400">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-zinc-100">Something went wrong</h1>
          <p className="mt-2 text-sm text-zinc-400">
            The dashboard hit an unexpected error. Your session is still active — try reloading the page.
          </p>
          {this.state.message ? (
            <p className="mt-3 break-words rounded-md bg-zinc-950 px-3 py-2 text-left font-mono text-xs text-zinc-500">
              {this.state.message}
            </p>
          ) : null}
          <div className="mt-6 flex justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="ub-btn ub-btn-primary justify-center"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="ub-btn ub-btn-secondary justify-center"
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
